import { useRouter } from "next/router";
import Image from "next/image";
import { numericToStringThousands } from "../../../../utils/number";
import { Product } from "../../../../stores/product-store/product-store.model";
import { trackEvents } from "../../../../utils/analytics";
import { SellerProfile } from "../../../../stores/seller-store/seller-store.model";
import { SellerPageRouter } from "../../page-router.props";
import { postMessageToBuyerApp } from "../../../../utils/post-message";

interface Props {
  sellerData: SellerProfile;
  product: Product;
  containerClassName?: string;
  onClickBuy: () => void;
}

export const ProductGridItem = ({
  sellerData,
  product,
  containerClassName,
  onClickBuy,
}: Props) => {
  const router: SellerPageRouter = useRouter();
  const { seller, ...query } = router.query;
  const featuredProductImageUrl =
    !!product.productImages.length &&
    product.productImages.find((image) => image.imageUrl && image.featured)
      ?.imageUrl;
  const imgSrc = featuredProductImageUrl || "/img/common/default-product.svg";

  const handleGoToDetail = () => {
    if (router.query.fromapp) {
      postMessageToBuyerApp("product-detail", product.id);
    } else {
      router.push({
        pathname: `${seller}/${product.id?.replace(/PRD-/g, "")}`,
        query,
      });
    }

    if (sellerData?.sellerJopan) {
      trackEvents({
        sellerUsername: sellerData?.User?.username,
        eventName: "produk_klik_detail",
        eventCategory: "order",
        eventLabel: "reorder",
        eventValue: {
          productId: product?.id,
        },
        moengageContextId: product?.id,
        trackers: ["GTM", "AF", "ME"],
      });
    }
  };

  const renderRankedLabel = () => {
    if (!product.ranked) {
      return null;
    }

    return (
      <>
        <div className="bg-primary-50 text-custombg absolute top-0 right-0 text-center text-label-xs p-1.5 font-bold select-none">
          #{product.ranked}
        </div>
        <div className="bg-primary absolute bottom-0 inset-x-0 text-custombg text-center text-label-xs p-1 font-bold uppercase select-none">
          TERLARIS
        </div>
      </>
    );
  };
  const renderPrice = () => {
    let productUnit = product?.baseUnits?.find((unit) => (unit.featured));
    if (!productUnit) {
      productUnit =
        !!product.baseUnits?.length &&
        product.baseUnits.reduce((prev, curr) =>
          prev.price < curr.price ? prev : curr
        );
    }
    const normalPrice = `Rp${numericToStringThousands(productUnit?.price)}/${productUnit?.unit
      }`;
    const discountPrice = `Rp${numericToStringThousands(
      productUnit?.discountPrice
    )}/${productUnit?.unit}`;
    const discountPercentage = `${Number(
      (1 - productUnit.discountPrice / productUnit.price) * 100
    ).toFixed(1)}%`;
    if (productUnit.discountPrice) {
      return (
        <>
          <div className="flex items-center mt-1">
            <span className="text-center text-label-xs font-bold rounded-full px-2 py-1 text-primary bg-primary-50">
              {discountPercentage}
            </span>
            <span className="ml-1 line-through text-label-xs font-bold text-secondary-gray">
              {normalPrice}
            </span>
          </div>
          <div className="text-label-md mt-1 font-bold text-primary">
            {discountPrice}
          </div>
        </>
      );
    } else {
      return (
        <div className="text-label-md mt-1 font-bold text-primary">
          {normalPrice}
        </div>
      );
    }
  };

  const renderBoughtCount = () => {
    if (!product.productBought) {
      return null;
    }

    const boughtCount =
      product.productBought > 999 ? "999+" : product.productBought;

    return <div className="mt-1 text-2xs">Terjual {`${boughtCount}`}</div>;
  };

  return (
    <div className={containerClassName}>
      <div className="relative shadow rounded cursor-pointer">
        <div onClick={handleGoToDetail}>
          <div className="relative rounded-t pb-full overflow-hidden">
            <Image
              src={imgSrc}
              alt={product.name}
              layout="fill"
              className="absolute inset-0 size-full object-cover"
              id="product-item-card-featured-image"
              data-testid="product-item-card-featured-image"
            />
            {renderRankedLabel()}
          </div>
          <div
            className="px-3 py-2"
            id="product-item-card-info"
            data-testid="product-item-card-info"
          >
            <div className="text-label-md font-medium">{product.name}</div>
            {renderPrice()}
            {renderBoughtCount()}
          </div>
        </div>
        <button
          onClick={() => onClickBuy()}
          className="border bg-primary w-full rounded text-label-sm p-2 text-custombg border-primary font-bold"
          id="product-item-buy-btn"
          data-testid="product-item-buy-btn"
        >
          BELI
        </button>
      </div>
    </div>
  );
};
